import { useCallback, useEffect, useId, useState } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import StopIcon from '@mui/icons-material/Stop';
import {
  isSpeechSupported,
  speakText,
  stopSpeaking,
  stripMarkdownForSpeech,
} from '../../../utils/readAloud';

/**
 * Compact control to read learner-facing text aloud (Web Speech API).
 */
export default function ReadAloudButton({
  text,
  label = 'Read aloud',
  stopLabel = 'Stop reading',
  size = 'small',
  sx,
  disabled = false,
}) {
  const reactId = useId();
  const [speaking, setSpeaking] = useState(false);
  const supported = isSpeechSupported();
  const plain = stripMarkdownForSpeech(text);
  const canSpeak = supported && Boolean(plain) && !disabled;

  useEffect(() => () => stopSpeaking(), []);

  useEffect(() => {
    stopSpeaking();
    setSpeaking(false);
  }, [plain]);

  const toggle = useCallback(() => {
    if (!canSpeak && !speaking) return;
    if (speaking) {
      stopSpeaking();
      setSpeaking(false);
      return;
    }
    speakText(plain, {
      onStart: () => setSpeaking(true),
      onEnd: () => setSpeaking(false),
      onError: () => setSpeaking(false),
    });
  }, [canSpeak, plain, speaking]);

  if (!supported || !plain) return null;

  return (
    <Tooltip title={speaking ? stopLabel : label}>
      <span>
        <IconButton
          size={size}
          color={speaking ? 'primary' : 'default'}
          onClick={toggle}
          disabled={!canSpeak && !speaking}
          aria-label={speaking ? stopLabel : label}
          aria-pressed={speaking}
          data-testid="read-aloud-button"
          data-speaking={speaking ? 'true' : 'false'}
          data-read-aloud-id={reactId}
          sx={sx}
        >
          {speaking ? <StopIcon fontSize="inherit" /> : <VolumeUpIcon fontSize="inherit" />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

/** Labeled button for primary lesson actions (e.g. Listen to this step). */
export function ReadAloudTextButton({
  text,
  children = 'Listen',
  stopChildren = 'Stop',
  startIcon,
  ...buttonProps
}) {
  const [speaking, setSpeaking] = useState(false);
  const supported = isSpeechSupported();
  const plain = stripMarkdownForSpeech(text);

  useEffect(() => () => stopSpeaking(), []);
  useEffect(() => {
    stopSpeaking();
    setSpeaking(false);
  }, [plain]);

  if (!supported || !plain) return null;

  return (
    <Button
      variant="text"
      startIcon={speaking ? <StopIcon /> : (startIcon || <VolumeUpIcon />)}
      data-testid="read-aloud-text-button"
      data-speaking={speaking ? 'true' : 'false'}
      aria-pressed={speaking}
      {...buttonProps}
      onClick={(e) => {
        buttonProps.onClick?.(e);
        if (speaking) {
          stopSpeaking();
          setSpeaking(false);
          return;
        }
        speakText(plain, {
          onStart: () => setSpeaking(true),
          onEnd: () => setSpeaking(false),
          onError: () => setSpeaking(false),
        });
      }}
    >
      {speaking ? stopChildren : children}
    </Button>
  );
}
